"use client";

import { useState, useEffect, useCallback, useRef } from 'react';
import { getSoftlockClient } from './core';
import {
  UseAccessKeyOptions,
  UseAccessKeyResult,
  ValidationResult,
} from './types';

/**
 * React hook for access key validation
 */
export function useAccessKey(
  initialKey?: string,
  options: UseAccessKeyOptions = {}
): UseAccessKeyResult {
  const {
    autoValidate = false,
    refetchInterval,
    disableCache = false,
  } = options;

  const [result, setResult] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentKey, setCurrentKey] = useState<string | undefined>(initialKey);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const isInitialMount = useRef(true);

  const validate = useCallback(async (key: string): Promise<ValidationResult> => {
    if (!key) {
      const errorResult: ValidationResult = {
        valid: false,
        error: 'No key provided',
      };
      setResult(errorResult);
      setError('No key provided');
      return errorResult;
    }

    setLoading(true);
    setError(null); try {
      const client = getSoftlockClient();

      // Temporarily disable cache if requested
      if (disableCache) {
        client.clearCache();
      }

      const validationResult = await client.validateKey(key);

      // Always set state in development mode to handle React Strict Mode
      if (process.env.NODE_ENV === 'development' || mountedRef.current) {
        setResult(validationResult);
        setCurrentKey(key);

        if (!validationResult.valid && validationResult.error) {
          setError(validationResult.error);
        }
      }

      return validationResult;
    } catch (err) {
      console.error('[useAccessKey] Validation error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Validation failed';
      const errorResult: ValidationResult = {
        valid: false,
        error: errorMessage,
      };

      if (mountedRef.current || process.env.NODE_ENV === 'development') {
        setResult(errorResult);
        setError(errorMessage);
      }

      return errorResult;
    } finally {
      if (mountedRef.current || process.env.NODE_ENV === 'development') {
        setLoading(false);
      }
    }
  }, [disableCache]);

  const clear = useCallback(() => {
    setResult(null);
    setError(null);
    setCurrentKey(undefined);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const retry = useCallback(() => {
    if (currentKey) {
      validate(currentKey);
    }
  }, [currentKey, validate]);

  // Auto-validate on mount if key is provided
  useEffect(() => {
    if (autoValidate && initialKey) {
      validate(initialKey);
    }
  }, [autoValidate, initialKey, validate]);

  // Set up refetch interval
  useEffect(() => {
    if (refetchInterval && currentKey) {
      intervalRef.current = setInterval(() => {
        validate(currentKey);
      }, refetchInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [refetchInterval, currentKey, validate]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true; // Ensure it's set to true on mount

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []); return {
    result,
    loading,
    error,
    validate,
    clear,
    retry,
  };
}

/**
 * Hook for managing user access state
 */
export function useUserAccess(userId?: string) {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [userKey, setUserKey] = useState<string | undefined>();

  const { result, loading, error, validate } = useAccessKey(userKey, {
    autoValidate: true,
  });

  useEffect(() => {
    if (result) {
      setHasAccess(result.valid);
    }
  }, [result]);

  const checkAccess = useCallback((key: string) => {
    setUserKey(key);
    return validate(key);
  }, [validate]);

  const revokeAccess = useCallback(() => {
    setHasAccess(false);
    setUserKey(undefined);
  }, []);

  return {
    hasAccess,
    loading,
    error,
    checkAccess,
    revokeAccess,
    userKey,
    validationResult: result,
  };
}

/**
 * Hook for protecting components based on access key validation
 */
export function useAccessGuard(
  keyValue?: string,
  options: {
    redirectUrl?: string;
    onUnauthorized?: () => void;
    fallback?: React.ComponentType;
  } = {}
) {
  const { result, loading, error, validate } = useAccessKey(keyValue, {
    autoValidate: !!keyValue,
  });

  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    if (result) {
      const authorized = result.valid;
      setIsAuthorized(authorized);

      if (!authorized && options.onUnauthorized) {
        options.onUnauthorized();
      }

      if (!authorized && options.redirectUrl) {
        window.location.href = options.redirectUrl;
      }
    }
  }, [result, options]);

  const checkKey = useCallback((key: string) => {
    return validate(key);
  }, [validate]); return {
    isAuthorized,
    loading,
    error,
    checkKey,
    validationResult: result,
  };
}
